"use client"

import Link from "next/link"
import type { ComponentPropsWithoutRef, ReactNode } from "react"

type Variant = "default" | "elevated" | "outlined" | "tinted"
type Tone = "neutral" | "red" | "orange" | "green" | "blue" | "amber" | "violet"
type Padding = "sm" | "md" | "lg" | "none"
type Radius = "sm" | "md" | "lg"

type CommonProps = {
  variant?: Variant
  tone?: Tone
  padding?: Padding
  radius?: Radius
  interactive?: boolean
  accent?: boolean
  children: ReactNode
  className?: string
}

type CardAsDiv = CommonProps & { href?: undefined } & Omit<
  ComponentPropsWithoutRef<"div">,
  keyof CommonProps
>

type CardAsLink = CommonProps & { href: string; external?: boolean } & Omit<
  ComponentPropsWithoutRef<"a">,
  keyof CommonProps | "href"
>

export type CardProps = CardAsDiv | CardAsLink

const PAD_CLS: Record<Padding, string> = {
  none: "",
  sm: "p-4",
  md: "p-5",
  lg: "p-7 sm:p-8",
}

const RADIUS_CLS: Record<Radius, string> = {
  sm: "rounded-[14px]",
  md: "rounded-[16px]",
  lg: "rounded-[20px]",
}

const VARIANT_CLS: Record<Variant, string> = {
  default:
    "bg-[var(--redy-surface)] border border-[var(--redy-border)] [box-shadow:var(--shadow-card)]",
  elevated:
    "bg-[var(--redy-surface)] border border-[var(--redy-border)] [box-shadow:var(--shadow-card-hover)]",
  outlined:
    "bg-transparent border border-[var(--redy-border)]",
  tinted:
    "border", // border-color set by tone
}

const TONE_TINT: Record<
  Tone,
  { bg: string; border: string; text: string }
> = {
  neutral: { bg: "#F7F5F1", border: "#EAE7E1", text: "#1A1A1A" },
  red:     { bg: "#FFE9EC", border: "rgba(215,38,61,0.20)",  text: "#D7263D" },
  orange:  { bg: "#FFF1E7", border: "rgba(244,114,43,0.20)", text: "#C75A1F" },
  green:   { bg: "#EAFAF0", border: "rgba(46,204,113,0.22)", text: "#1A7A3C" },
  blue:    { bg: "#F0F4FF", border: "rgba(74,122,232,0.22)", text: "#2850A0" },
  amber:   { bg: "#FFF8E1", border: "rgba(244,168,31,0.22)", text: "#9C6800" },
  violet:  { bg: "#F1ECFF", border: "rgba(124,58,237,0.22)", text: "#5B21B6" },
}

const BASE_INTERACTIVE =
  "transition-all duration-200 ease-out hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--redy-red)] focus-visible:ring-offset-2"

export function Card(props: CardProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p = props as any
  const variant: Variant = p.variant ?? "default"
  const tone: Tone = p.tone ?? "neutral"
  const padding: Padding = p.padding ?? "md"
  const radius: Radius = p.radius ?? "md"
  const interactive: boolean = p.interactive ?? typeof p.href === "string"
  const accent: boolean = p.accent ?? false

  const cls = [
    "relative overflow-hidden flex flex-col",
    RADIUS_CLS[radius],
    VARIANT_CLS[variant],
    PAD_CLS[padding],
    interactive ? BASE_INTERACTIVE : "",
    p.className ?? "",
  ]
    .filter(Boolean)
    .join(" ")

  const tintedStyle =
    variant === "tinted"
      ? {
          background: TONE_TINT[tone].bg,
          borderColor: TONE_TINT[tone].border,
          color: TONE_TINT[tone].text,
        }
      : undefined

  const inner = (
    <>
      {accent && (
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: "var(--redy-gradient)" }}
          aria-hidden
        />
      )}
      {p.children}
    </>
  )

  if (typeof p.href === "string") {
    const {
      variant: _v,
      tone: _t,
      padding: _p,
      radius: _r,
      interactive: _i,
      accent: _a,
      children: _c,
      className: _cls,
      href,
      external,
      ...rest
    } = p
    if (external) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cls}
          style={tintedStyle}
          {...rest}
        >
          {inner}
        </a>
      )
    }
    return (
      <Link href={href} className={cls} style={tintedStyle} {...rest}>
        {inner}
      </Link>
    )
  }

  const {
    variant: _v,
    tone: _t,
    padding: _p,
    radius: _r,
    interactive: _i,
    accent: _a,
    children: _c,
    className: _cls,
    ...rest
  } = p
  return (
    <div className={cls} style={tintedStyle} {...rest}>
      {inner}
    </div>
  )
}

// ── KpiCard ─────────────────────────────────────────────────────────────────
// Compact KPI tile. For the editorial hero with sparkline + clamp font use
// HubHeroKpi from components/admin/EditorialHub.
//
// P-4 (audit 2026-05-28): added `icon` slot and `valueAccent` tone so
// the dashboard and inventory MetricCard re-implementations can be
// replaced. `delta` continues to drive the up/down chip; `valueAccent`
// is independent and tints just the value text (used for low-stock /
// margin-loss highlights).

export type KpiCardValueAccent = "default" | "success" | "amber" | "red"

const VALUE_ACCENT_COLOR: Record<KpiCardValueAccent, string> = {
  default: "var(--redy-charcoal)",
  success: "var(--redy-success)",
  amber: "var(--redy-amber-deep)",
  red: "var(--redy-error)",
}

export type KpiCardProps = {
  eyebrow?: string
  label: string
  value: string | number
  delta?: { pct: number; up: boolean } | null
  hint?: string
  accent?: boolean
  /** Top-right icon slot — useful for category tiles in dashboards. */
  icon?: ReactNode
  /** Tints just the value text. Independent of delta. */
  valueAccent?: KpiCardValueAccent
  className?: string
  /** Si está, la tarjeta es un enlace (delega en el Card link existente). */
  href?: string
}

export function KpiCard({
  eyebrow,
  label,
  value,
  delta,
  hint,
  accent = false,
  icon,
  valueAccent = "default",
  className = "",
  href,
}: KpiCardProps) {
  return (
    <Card
      variant="default"
      padding="md"
      radius="md"
      accent={accent}
      className={className}
      {...(href ? { href } : {})}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-[var(--redy-muted)] mb-1">
              {eyebrow}
            </p>
          )}
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[var(--redy-muted)]">
            {label}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {delta != null && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-[3px] rounded-full"
              style={{
                background: delta.up ? "var(--redy-success-bg)" : "var(--redy-error-bg)",
                color: delta.up ? "var(--redy-success)" : "var(--redy-error)",
              }}
            >
              {delta.up ? "▲" : "▼"}
              <span className="tabular-nums">{Math.abs(delta.pct).toFixed(1)}%</span>
            </span>
          )}
          {icon && (
            <div
              className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[var(--redy-muted)]"
              style={{ background: "var(--redy-warm-white)" }}
              aria-hidden
            >
              {icon}
            </div>
          )}
        </div>
      </div>
      <p
        className="mt-3 text-[28px] font-extrabold leading-none tabular-nums"
        style={{ letterSpacing: "-0.02em", color: VALUE_ACCENT_COLOR[valueAccent] }}
      >
        {value}
      </p>
      {hint && (
        <p className="mt-1.5 text-[11px] text-[var(--redy-secondary)]">{hint}</p>
      )}
    </Card>
  )
}
